import { FC, useMemo, useState } from "react";
import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";

import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";
import { ResponsiveTable, Table, TableContent, Th } from "@/components/Table";
import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import InfiniteLoader from "@/components/InfiniteLoader";
import { PaginatedResponse, ReportResource, Role } from "@/types/resources";
import { api } from "@/utils/api";
import ReportRow from "./ReportRow";

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px;
    font-size: 11px;
    vertical-align: middle;

    &:nth-child(1) {
      padding-left: 24px;
      width: 46%;
    }
  }
`;

interface Props {
  groupId: number;
  userRole: Role;
  isGroupRestricted: boolean;
}

const ReportsTable: FC<Props> = ({ groupId, userRole, isGroupRestricted }) => {
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 400);

  const fetchReports = ({ pageParam = `/v1/reports` }) => {
    return api.get<never, PaginatedResponse<ReportResource>>(pageParam, {
      params: {
        ...(!!debouncedSearch && {
          "filter[search]": debouncedSearch,
        }),
        "filter[reports_group_id]": groupId,
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<ReportResource>>(
    [
      "reports",
      {
        ...(!!debouncedSearch && { search: debouncedSearch }),
        groupId,
      },
    ],
    fetchReports,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const reports = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<ReportResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <SearchBar value={search} setValue={setSearch} />
      <SectionHeading>Reports list</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Report Date</Th>
              <Th>Note</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={3}
              isError={isError}
              hasData={!!reports?.length}
            >
              {reports?.map((report) => (
                <ReportRow
                  key={report.id}
                  report={report}
                  userRole={userRole}
                  isGroupRestricted={isGroupRestricted}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default ReportsTable;
